// /admin/flags — feature flags, inside the admin area (moved from the
// standalone /flags ContentPage 2026-07-25 per Nika "put flags page in
// /admin"). Lists EVERY product feature from the FEATURE_FLAGS registry
// (functions/featureFlags.js), grouped, with iOS-style switches; core
// "Basics" are locked always-on; rows with subs detail-down into a wash
// card. Overrides persist per-workspace in Mongo
// (workspace.settings.featureFlags via sdk.updateWorkspaceSettings) and
// mirror onto root state — surfaces gate themselves with
// featureEnabled(s.root, key). Admin-gated like every /admin page
// (shouldRedirectNonAdmin — workspace owners/admins; superadmin not
// required, flags are per-workspace personalization).
//
// The flag GROUPS and the Voice block are registered components
// (components/FlagRow.js FlagGroupSection, components/FlagsVoiceCard.js), fed
// at RENDER time from `featureFlagGroupsWithFlags`. They used to be
// module-local element factories mapped into a module-scope `children:` array
// here, which made the entire workspace-shell project uncompilable by frank —
// see tickets/fable.md and the comment on featureFlagGroupsWithFlags in
// functions/featureFlags.js. Nothing on this page may dereference the registry
// at module scope again.
import { hydrateFeatureFlags } from '../functions/featureFlags.js'

// The route gate: not a workspace owner or admin → home.
const _flagsRouteGate = (el, s) => {
  if (el.call('shouldRedirectNonAdmin', s)) el.call('routeGateBounce', '/')
}

export const flags = {
  extends: 'AppPage',
  metadata: (el) => ({ title: el.call('pageTitle', 'admin.flags.page.title') }),

  // shouldRedirectNonAdmin fails open until identity and the roster land, and
  // a cold deep link lands them after onInit — so the page subscribes to
  // every gate input (functions/roleHelpers.js `routeGateKeys`) and a
  // subscription pass runs the gate ALONE; the `false` vetoes the rest of it
  // (F2, 2026-10-31; the whole note is on pages/admin/admin.js). Before it,
  // the gate ran once, at onInit.
  onInit: (el, s) => _flagsRouteGate(el, s),
  subscribeTo: (el) => el.call('routeGateKeys'),
  onBeforeUpdate: (el, s, ctx, opts) => {
    if (!opts || !opts.updateBySubscription) return
    _flagsRouteGate(el, s)
    return false
  },
  onCreate: (el, s) => {
    hydrateFeatureFlags(s.root)
  },

  AdminSidebar: {},

  AdminMain: {
    extends: 'AppMain',
    padding: '0',

    Header: {
      extends: 'AdminPageHeader',
      adminIcon: 'switching',
      LeftGroup: {
        // One title per host (design-direction.md §3): "Feature flags" is the
        // pane bar word for word in every language, so it yields under one.
        Title: {
          extends: 'PageHeaderTitle',
          hide: (el, s) => el.call('paneTitleBarShown', s),
          text: '{{ admin.flags.title | polyglot }}'
        }
      }
    },

    Body: {
      extends: 'AdminPageBody',
      overflow: 'auto',
      gap: 'A',
      '@mobileL': { padding: 'A' },

      Column: {
        flow: 'y',
        gap: 'A',
        width: '100%',
        // Settings rows read best on a bounded measure.
        maxWidth: '100%',

        Intro: {
          tag: 'p',
          margin: '0',
          fontSize: 'Z1',
          lineHeight: '1.5',
          color: 'paragraph',
          text: '{{ admin.flags.intro | polyglot }}'
        },

        FlagGroups: {
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, var(--spacing-H)), 1fr))',
          alignItems: 'start',
          gap: 'A',
          childExtends: 'FlagGroupSection',
          childrenAs: 'state',
          childProps: {
            Card: {
              childProps: {
                Top: { flow: 'y', align: 'stretch start', gap: 'Z' }
              }
            }
          },
          children: (el) => el.call('featureFlagGroupsWithFlags')
        },
      }
    }
  }
}

// Back-compat: the pre-admin URL. Old links, the localStorage-restored
// ?page= slots, and muscle memory land here — bounce to the admin home.
// It carries the title of the page it opens: a ?page= slot pane and the AI
// route chip read the route's def (with none, the chip said "Flags" in a
// Georgian session).
export const flagsRedirect = {
  metadata: (el) => ({ title: el.call('pageTitle', 'admin.flags.page.title') }),
  onRender: (el) => {
    const root = el.call('findRouteRoot', el)
    if (root) el.router('/admin/flags', root, {}, { pushState: false })
  }
}
